/**
 * use-manual-quotes.ts — Hooks cho Platform → Báo giá thủ công (super-admin).
 * Backend: ecdp_backend_source/src/modules/platform/manual-quote/manual-quote-routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type {
  Breakdown, ManualQuote, ManualQuoteListItem, OrgContext, Template, formToInput,
} from '@/pages/platform/manual-quote/manual-quote-helpers'

const KEY = ['platform', 'manual-quotes'] as const
type QuoteInput = ReturnType<typeof formToInput>

export function useManualQuotes(params: { status?: string; q?: string; page?: number }) {
  return useQuery<{ items: ManualQuoteListItem[]; total: number; page: number; pageSize: number }>({
    queryKey: [...KEY, 'list', params],
    queryFn: async () => (await platformApi.get('/platform/manual-quotes', { params })).data,
    placeholderData: (prev) => prev,
  })
}

export function useManualQuote(id: string | undefined) {
  return useQuery<ManualQuote>({
    queryKey: [...KEY, 'one', id],
    enabled: !!id,
    queryFn: async () => (await platformApi.get(`/platform/manual-quotes/${id}`)).data,
  })
}

export async function previewManualQuote(input: QuoteInput): Promise<Breakdown> {
  return (await platformApi.post('/platform/manual-quotes/preview', input)).data
}

export function useQuoteTemplate() {
  return useQuery<{ template: Template; updatedAt: string | null }>({
    queryKey: [...KEY, 'template'],
    queryFn: async () => (await platformApi.get('/platform/manual-quotes/template')).data,
    staleTime: 60_000,
  })
}

export function useSaveQuoteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (template: Template) => (await platformApi.put('/platform/manual-quotes/template', { template })).data,
    onSuccess: (data) => qc.setQueryData([...KEY, 'template'], data),
  })
}

export async function searchQuoteOrgs(search: string) {
  const { data } = await platformApi.get<{ items: { id: string; name: string; plan: string | null; expiresAt: string | null; ownerEmail: string | null; ownerName: string | null }[] }>(
    '/platform/manual-quotes/orgs', { params: { search } },
  )
  return data.items
}

export async function loadOrgContext(orgId: string): Promise<OrgContext> {
  return (await platformApi.get(`/platform/manual-quotes/org-context/${orgId}`)).data
}

export function useSaveManualQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: QuoteInput }): Promise<ManualQuote> =>
      (id ? await platformApi.put(`/platform/manual-quotes/${id}`, input) : await platformApi.post('/platform/manual-quotes', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useQuoteAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, action, body }: { id: string; action: 'send' | 'cancel' | 'duplicate'; body?: unknown }): Promise<ManualQuote> =>
      (await platformApi.post(`/platform/manual-quotes/${id}/${action}`, body ?? {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export interface ConfirmPaidBody {
  idempotencyKey: string
  paymentRef: string
  paidDate: string
  reason: string
  invoice: Record<string, unknown> | null
  newOrg: { orgName: string; ownerFullName: string; ownerEmail: string; ownerPassword: string; referralCode: string } | null
}

export function useConfirmPaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ConfirmPaidBody }) =>
      (await platformApi.post<{ replay: boolean; quote: ManualQuote; orgCreated: boolean; referralError: string | null; warnings: string[] }>(
        `/platform/manual-quotes/${id}/confirm-paid`, body, { headers: { 'Idempotency-Key': body.idempotencyKey } },
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
